import React, {
  Children,
  Fragment,
  cloneElement,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import * as ReactVirtualized from 'react-virtualized';

import clsx from 'clsx';

import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from '@gogame/ui/Icon';
import IconButton from '@gogame/ui/IconButton';
import { makeStyles } from '@gogame/ui/styles';

import styled from '../styled';

const GalleryItemContainer = styled(
  React.forwardRef(({ withBorder: _, ...props }, ref) => {
    return <div {...props} ref={ref} />;
  })
)`
  @media screen and (min-width: 1440px) {
    border-left: ${({ withBorder }) =>
      withBorder ? `1px solid ${(theme) => theme.palette.divider}` : 'none'};
  }
`;

const MIN_WIDTH_VIEW_SLIDER = 1440;

const useStyles = makeStyles((theme) => ({
  buttonSlide: {
    position: 'absolute',
    width: '32px',
    height: '32px',
    bottom: '142px',
    zIndex: 1,
    background: theme.palette.divider,
    boxShadow: theme.shadows[2],
  },
  buttonSlidePrev: {
    left: '0',
    right: 'auto',
  },
  buttonSlideNext: {
    left: 'auto',
    right: '0',
  },
}));

const ButtonSlide = ({ children, ...props }) => (
  <IconButton color={'primary'} {...props}>
    {children}
  </IconButton>
);

export const GalleryItem = ({ children, style, withBorder, ...props }) => {
  return (
    <GalleryItemContainer
      withBorder={withBorder}
      style={{
        ...style,
        width: '100%',
      }}
      {...props}
    >
      {children}
    </GalleryItemContainer>
  );
};

export const Gallery = ({ children }) => {
  const [sliderCurrent, setSliderCurrent] = useState(0);
  const initializedSlides = useRef([]);

  const classes = useStyles();

  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  const childrenLastIndex = useMemo(
    () => Math.max(0, childrenArray.length - 1),
    [childrenArray]
  );

  useEffect(() => {
    if (sliderCurrent > childrenLastIndex) {
      setSliderCurrent(childrenLastIndex);
    }
  }, [sliderCurrent, childrenLastIndex]);

  useEffect(() => {
    if (initializedSlides.current.indexOf(sliderCurrent) === -1) {
      initializedSlides.current.push(sliderCurrent);
    }
  }, [sliderCurrent]);

  const handleChangeSliderPrev = useCallback(() => {
    setSliderCurrent((index) => Math.max(0, index - 1));
  }, []);

  const handleChangeSliderNext = useCallback(() => {
    setSliderCurrent((index) => Math.min(childrenLastIndex, index + 1));
  }, [childrenLastIndex]);

  return (
    <ReactVirtualized.AutoSizer
      style={{ display: 'flex', height: 'auto', width: 'auto' }}
    >
      {({ width }) => {
        const canSlide = width <= MIN_WIDTH_VIEW_SLIDER;
        return (
          <Fragment>
            {children.map((item, index) => {
              if (item.type !== GalleryItem) {
                throw Error('Children must be only <GalleryItem />');
              }
              //do not render(and load his Chart) if a slide was not active
              if (canSlide && initializedSlides.current.indexOf(index) === -1) {
                return null;
              }

              return cloneElement(item, {
                style: {
                  ...item.props.style,
                  display: canSlide && sliderCurrent !== index ? 'none' : null,
                },
              });
            })}
            {canSlide ? (
              <Fragment>
                {0 !== sliderCurrent ? (
                  <ButtonSlide
                    className={clsx(
                      classes.buttonSlide,
                      classes.buttonSlidePrev
                    )}
                    onClick={handleChangeSliderPrev}
                  >
                    <KeyboardArrowLeftIcon />
                  </ButtonSlide>
                ) : null}
                {childrenLastIndex !== sliderCurrent ? (
                  <ButtonSlide
                    className={clsx(
                      classes.buttonSlide,
                      classes.buttonSlideNext
                    )}
                    onClick={handleChangeSliderNext}
                  >
                    <KeyboardArrowRightIcon />
                  </ButtonSlide>
                ) : null}
              </Fragment>
            ) : null}
          </Fragment>
        );
      }}
    </ReactVirtualized.AutoSizer>
  );
};
